import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  message,
  Tag,
  Descriptions,
  Typography,
  Checkbox,
  Form,
  Input,
  InputNumber,
} from 'antd';
import { ReloadOutlined, EyeOutlined, TeamOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';

const { Text } = Typography;

const GestionRoles = () => {
  const { hasPermission } = usePermissions();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [modalPermisosVisible, setModalPermisosVisible] = useState(false);
  const [rolSeleccionado, setRolSeleccionado] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [modulos, setModulos] = useState({});
  const [permisosLoading, setPermisosLoading] = useState(false);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState(new Set());
  const [savingPermisos, setSavingPermisos] = useState(false);
  const [incluirInactivos, setIncluirInactivos] = useState(false);
  const [modalRolVisible, setModalRolVisible] = useState(false);
  const [editingRolId, setEditingRolId] = useState(null);
  const [savingRol, setSavingRol] = useState(false);
  const [formRol] = Form.useForm();

  const loadRoles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ withCounts: 'true' });
      if (incluirInactivos) params.set('incluir_inactivos', 'true');
      const res = await apiClient.get(`/gestion/roles?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setRoles(Array.isArray(data) ? data : []);
      } else {
        message.error('Error al cargar roles');
      }
    } catch (e) {
      console.error(e);
      message.error('Error al cargar roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, [incluirInactivos]);

  const handleVerDetalle = async (record) => {
    setRolSeleccionado(null);
    setModalDetalleVisible(true);
    setDetalleLoading(true);
    try {
      const res = await apiClient.get(`/gestion/roles/${record.rol_id}`);
      if (res.ok) {
        const data = await res.json();
        setRolSeleccionado(data);
      } else {
        message.error('Error al cargar el rol');
        setModalDetalleVisible(false);
      }
    } catch (e) {
      message.error('Error al cargar el rol');
      setModalDetalleVisible(false);
    } finally {
      setDetalleLoading(false);
    }
  };

  const handleAbrirEditarPermisos = async (record) => {
    setRolSeleccionado(null);
    setModalPermisosVisible(true);
    setPermisosLoading(true);
    setSelectedPermissionIds(new Set());
    try {
      const [roleRes, modulosRes] = await Promise.all([
        apiClient.get(`/gestion/roles/${record.rol_id}`),
        apiClient.get('/gestion/permisos/modulos'),
      ]);
      if (roleRes.ok && modulosRes.ok) {
        const roleData = await roleRes.json();
        const modulosData = await modulosRes.json();
        setRolSeleccionado(roleData);
        setModulos(modulosData && typeof modulosData === 'object' ? modulosData : {});
        setSelectedPermissionIds(new Set(roleData.permissionIds || []));
      } else {
        message.error('Error al cargar datos');
        setModalPermisosVisible(false);
      }
    } catch (e) {
      message.error('Error al cargar datos');
      setModalPermisosVisible(false);
    } finally {
      setPermisosLoading(false);
    }
  };

  const handleTogglePermiso = (perId, checked) => {
    setSelectedPermissionIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(perId);
      else next.delete(perId);
      return next;
    });
  };

  const handleGuardarPermisos = async () => {
    if (!rolSeleccionado) return;
    setSavingPermisos(true);
    try {
      const res = await apiClient.put(`/gestion/roles/${rolSeleccionado.rol_id}/permissions`, {
        permission_ids: Array.from(selectedPermissionIds),
      });
      if (res.ok) {
        message.success('Permisos actualizados');
        setModalPermisosVisible(false);
        loadRoles();
      } else {
        const err = await res.json().catch(() => ({}));
        message.error(err.message || 'Error al guardar');
      }
    } catch (e) {
      message.error('Error al guardar permisos');
    } finally {
      setSavingPermisos(false);
    }
  };

  const handleOpenNuevoRol = () => {
    setEditingRolId(null);
    formRol.resetFields();
    formRol.setFieldsValue({ rol_es_admin: false, rol_orden: 0 });
    setModalRolVisible(true);
  };

  const handleOpenEditarRol = (record) => {
    setEditingRolId(record.rol_id);
    formRol.setFieldsValue({
      rol_codigo: record.rol_codigo,
      rol_nombre: record.rol_nombre,
      rol_es_admin: record.rol_es_admin,
      rol_activo: record.rol_activo !== false,
      rol_orden: record.rol_orden ?? 0,
    });
    setModalRolVisible(true);
  };

  const handleGuardarRol = async () => {
    try {
      const values = await formRol.validateFields();
      setSavingRol(true);
      if (editingRolId) {
        const res = await apiClient.put(`/gestion/roles/${editingRolId}`, {
          rol_codigo: values.rol_codigo,
          rol_nombre: values.rol_nombre,
          rol_es_admin: values.rol_es_admin,
          rol_activo: values.rol_activo,
          rol_orden: values.rol_orden,
        });
        if (res.ok) {
          message.success('Rol actualizado');
          setModalRolVisible(false);
          loadRoles();
        } else {
          const err = await res.json().catch(() => ({}));
          message.error(err.message || 'Error al actualizar');
        }
      } else {
        const res = await apiClient.post('/gestion/roles', {
          rol_codigo: values.rol_codigo,
          rol_nombre: values.rol_nombre,
          rol_es_admin: values.rol_es_admin,
          rol_orden: values.rol_orden,
        });
        if (res.ok) {
          message.success('Rol creado. Asignale permisos desde "Editar permisos".');
          setModalRolVisible(false);
          loadRoles();
        } else {
          const err = await res.json().catch(() => ({}));
          message.error(err.message || 'Error al crear');
        }
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error(e.message || 'Error al guardar');
    } finally {
      setSavingRol(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'rol_id',
      key: 'rol_id',
      width: 70,
    },
    {
      title: 'Código',
      dataIndex: 'rol_codigo',
      key: 'rol_codigo',
      render: (c) => <Text code>{c}</Text>,
    },
    {
      title: 'Nombre',
      dataIndex: 'rol_nombre',
      key: 'rol_nombre',
    },
    {
      title: 'Es admin',
      dataIndex: 'rol_es_admin',
      key: 'rol_es_admin',
      width: 100,
      render: (v) => (v ? <Tag color="gold">Sí</Tag> : <Tag>No</Tag>),
    },
    {
      title: 'Activo',
      dataIndex: 'rol_activo',
      key: 'rol_activo',
      width: 80,
      render: (v) => (v !== false ? <Tag color="green">Sí</Tag> : <Tag color="red">No</Tag>),
    },
    {
      title: 'Permisos',
      key: 'permissionCount',
      width: 110,
      render: (_, record) =>
        record.rol_es_admin ? (
          <Tag color="green">Todos</Tag>
        ) : (
          <span>{record.permissionCount ?? 0} permisos</span>
        ),
    },
    {
      title: 'Usuarios',
      dataIndex: 'userCount',
      key: 'userCount',
      width: 100,
      render: (c) => (c != null ? `${c} usuario(s)` : '-'),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 280,
      render: (_, record) => (
        <>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleVerDetalle(record)}>
            Ver detalle
          </Button>
          {hasPermission('usuarios', 'editar') && (
            <Button type="link" icon={<EditOutlined />} onClick={() => handleOpenEditarRol(record)}>
              Editar rol
            </Button>
          )}
          {!record.rol_es_admin && hasPermission('usuarios', 'editar') && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleAbrirEditarPermisos(record)}
            >
              Editar permisos
            </Button>
          )}
        </>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#fff', fontSize: 24 }}>
            Roles del sistema
          </h1>
          <p style={{ color: '#b0b0b0', marginBottom: 16 }}>
            Cada rol define un conjunto de permisos. Los usuarios reciben permisos a través de los roles asignados.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <Button icon={<ReloadOutlined />} onClick={loadRoles} loading={loading}>
              Actualizar
            </Button>
            {hasPermission('usuarios', 'crear') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenNuevoRol}>
                Nuevo rol
              </Button>
            )}
            <Checkbox
              checked={incluirInactivos}
              onChange={(e) => setIncluirInactivos(e.target.checked)}
              style={{ color: '#b0b0b0' }}
            >
              Incluir roles inactivos
            </Checkbox>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={roles}
          loading={loading}
          rowKey="rol_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (t) => `Total: ${t} roles`,
          }}
        />

        <Modal
          title={
            rolSeleccionado ? (
              <>
                <TeamOutlined style={{ marginRight: 8 }} />
                {rolSeleccionado.rol_nombre}
              </>
            ) : (
              'Detalle del rol'
            )
          }
          open={modalDetalleVisible}
          onCancel={() => setModalDetalleVisible(false)}
          footer={null}
          width={600}
          confirmLoading={detalleLoading}
        >
          {detalleLoading && !rolSeleccionado ? (
            <div style={{ padding: 24, textAlign: 'center' }}>Cargando...</div>
          ) : rolSeleccionado ? (
            <>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="ID">{rolSeleccionado.rol_id}</Descriptions.Item>
                <Descriptions.Item label="Código">
                  <Text code>{rolSeleccionado.rol_codigo}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Nombre">{rolSeleccionado.rol_nombre}</Descriptions.Item>
                <Descriptions.Item label="Es admin">
                  {rolSeleccionado.rol_es_admin ? (
                    <Tag color="gold">Sí (todos los permisos)</Tag>
                  ) : (
                    'No'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Usuarios con este rol">
                  {rolSeleccionado.userCount ?? 0}
                </Descriptions.Item>
              </Descriptions>

              <div style={{ marginTop: 16 }}>
                <div style={{ marginBottom: 8, color: '#fff', fontWeight: 500 }}>
                  Permisos
                </div>
                {rolSeleccionado.rol_es_admin ? (
                  <Tag color="green">Este rol tiene todos los permisos del sistema.</Tag>
                ) : rolSeleccionado.permissionsByModule &&
                  Object.keys(rolSeleccionado.permissionsByModule).length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {Object.entries(rolSeleccionado.permissionsByModule).map(([modulo, perms]) => (
                      <div key={modulo}>
                        <div style={{ color: '#52c41a', marginBottom: 4, fontSize: 13 }}>
                          {modulo}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {(perms || []).map((p) => (
                            <Tag key={p.per_codigo}>
                              {p.per_descripcion || p.per_codigo}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Text type="secondary">Sin permisos asignados.</Text>
                )}
              </div>
            </>
          ) : null}
        </Modal>

        <Modal
          title={
            rolSeleccionado ? (
              <>
                <EditOutlined style={{ marginRight: 8 }} />
                Asignar permisos: {rolSeleccionado.rol_nombre}
              </>
            ) : (
              'Asignar permisos al rol'
            )
          }
          open={modalPermisosVisible}
          onCancel={() => setModalPermisosVisible(false)}
          onOk={handleGuardarPermisos}
          okText="Guardar"
          cancelText="Cancelar"
          width={560}
          confirmLoading={savingPermisos}
          destroyOnClose
        >
          {permisosLoading && !rolSeleccionado ? (
            <div style={{ padding: 24, textAlign: 'center' }}>Cargando...</div>
          ) : rolSeleccionado && rolSeleccionado.rol_es_admin ? (
            <Tag color="gold">El rol administrador tiene todos los permisos y no se puede editar.</Tag>
          ) : rolSeleccionado && Object.keys(modulos).length > 0 ? (
            <div style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {Object.entries(modulos).map(([modulo, permisos]) => (
                <div key={modulo}>
                  <div style={{ color: '#52c41a', marginBottom: 8, fontWeight: 500 }}>
                    {modulo}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {(permisos || []).map((p) => (
                      <Checkbox
                        key={p.per_id}
                        checked={selectedPermissionIds.has(p.per_id)}
                        onChange={(e) => handleTogglePermiso(p.per_id, e.target.checked)}
                      >
                        {p.per_descripcion || p.per_codigo}
                      </Checkbox>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </Modal>

        <Modal
          title={editingRolId ? 'Editar rol' : 'Nuevo rol'}
          open={modalRolVisible}
          onCancel={() => setModalRolVisible(false)}
          onOk={handleGuardarRol}
          okText="Guardar"
          cancelText="Cancelar"
          width={440}
          confirmLoading={savingRol}
          destroyOnClose
        >
          <Form form={formRol} layout="vertical">
            <Form.Item
              name="rol_codigo"
              label="Código"
              rules={[{ required: true, message: 'Requerido' }, { pattern: /^[a-z0-9_]+$/i, message: 'Solo letras, números y guión bajo' }]}
            >
              <Input placeholder="ej: cajero" disabled={!!editingRolId} />
            </Form.Item>
            <Form.Item
              name="rol_nombre"
              label="Nombre"
              rules={[{ required: true, message: 'Requerido' }]}
            >
              <Input placeholder="ej: Cajero" />
            </Form.Item>
            <Form.Item name="rol_es_admin" valuePropName="checked" label="Es administrador">
              <Checkbox>Este rol tiene todos los permisos del sistema</Checkbox>
            </Form.Item>
            {editingRolId && (
              <Form.Item name="rol_activo" valuePropName="checked" label="Activo">
                <Checkbox>Rol activo (visible al asignar a usuarios)</Checkbox>
              </Form.Item>
            )}
            <Form.Item name="rol_orden" label="Orden (para mostrar en listas)">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default GestionRoles;
